"use client";

import { useEffect, useState } from "react";

/* Membership status read — calls
 *   GET {backend}/api/v1/digital/insider-pass/status?email={email}
 * which the apex-flow-labs side serves from apex_insider_pass_subscribers
 * (email-indexed). Used on the catalog + product detail pages so members
 * see "Already unlocked — open in your library →" instead of the buy
 * button.
 *
 * Cache-safe: result memoized in sessionStorage for 5 minutes per email
 * so a logged-in member doesn't refetch on every page change. */

const BACKEND_BASE = (
  process.env.NEXT_PUBLIC_BACKEND_API_BASE || "https://www.apexflowlabs.com"
).replace(/\/$/, "");
const TTL_MS = 5 * 60 * 1000;

export type InsiderPassStatus = {
  active: boolean;
  plan?: "annual" | "monthly" | "charter" | "upsell" | null;
  current_period_end?: string | null;
  is_charter?: boolean;
  charter_number?: number | null;
  cancel_at_period_end?: boolean;
};

const EMPTY: InsiderPassStatus = { active: false };

function cacheKey(email: string): string {
  return `apex-insider-status:${email.toLowerCase()}`;
}

function readCache(email: string): InsiderPassStatus | null {
  try {
    const raw = sessionStorage.getItem(cacheKey(email));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      at: number;
      status: InsiderPassStatus;
    };
    if (Date.now() - parsed.at > TTL_MS) return null;
    return parsed.status;
  } catch {
    return null;
  }
}

function writeCache(email: string, status: InsiderPassStatus): void {
  try {
    sessionStorage.setItem(
      cacheKey(email),
      JSON.stringify({ at: Date.now(), status }),
    );
  } catch {
    /* sessionStorage unavailable (privacy mode etc.) — fail silent. */
  }
}

export function useInsiderPassStatus(
  email: string | null | undefined,
): {
  status: InsiderPassStatus;
  loading: boolean;
} {
  const [status, setStatus] = useState<InsiderPassStatus>(() => {
    if (!email) return EMPTY;
    return readCache(email) ?? EMPTY;
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!email) {
      setStatus(EMPTY);
      return;
    }
    const cached = readCache(email);
    if (cached) {
      setStatus(cached);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetch(
      `${BACKEND_BASE}/api/v1/digital/insider-pass/status?email=${encodeURIComponent(email)}`,
      { cache: "no-store" },
    )
      .then((r) => (r.ok ? r.json() : EMPTY))
      .then((j: InsiderPassStatus) => {
        if (cancelled) return;
        const next: InsiderPassStatus = {
          active: Boolean(j?.active),
          plan: j?.plan ?? null,
          current_period_end: j?.current_period_end ?? null,
          is_charter: Boolean(j?.is_charter),
          charter_number: j?.charter_number ?? null,
          cancel_at_period_end: Boolean(j?.cancel_at_period_end),
        };
        setStatus(next);
        writeCache(email, next);
      })
      .catch(() => {
        if (!cancelled) setStatus(EMPTY);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [email]);

  return { status, loading };
}
